import axios from "axios";
import { API_BASE_URL } from "../utils/config";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { firestore } from "../../services/firebase";

// Create a new bill
const createBill = async (user, billData) => {
  try {
    if (user) {
      const idTokenResult = await user.getIdTokenResult();
      if (
        idTokenResult.claims.admin === true ||
        idTokenResult.claims.subAdmin === true
      ) {
        const idToken = await user.getIdToken();
        const response = await axios.post(
          `${API_BASE_URL}api/bill/bills`,
          billData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
          }
        );
        return response;
      } else {
        throw {
          response: {
            data: {
              message: "User is not authorized to create bills.",
              type: "error",
            },
          },
        };
      }
    }
  } catch (error) {
    throw error;
  }
};

// Update a bill
const updateBill = async (user, billId, billData) => {
  try {
    if (!user) {
      return null;
    }

    const idTokenResult = await user.getIdTokenResult();
    if (
      idTokenResult.claims.admin === true ||
      idTokenResult.claims.subAdmin === true
    ) {
      const idToken = await user.getIdToken();
      const response = await axios.put(
        `${API_BASE_URL}api/bill/bills/${billId}`,
        billData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      return response;
    } else {
      throw {
        response: {
          data: {
            message: "User is not authorized to update bills.",
            type: "error",
          },
        },
      };
    }
  } catch (error) {
    throw error;
  }
};

// Delete a bill
const deleteBill = async (user, billId) => {
  try {
    if (!user) {
      return null;
    }

    const idTokenResult = await user.getIdTokenResult();
    if (
      idTokenResult.claims.admin === true ||
      idTokenResult.claims.subAdmin === true
    ) {
      const idToken = await user.getIdToken();
      const response = await axios.delete(
        `${API_BASE_URL}api/bill/bills/${billId}`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      return response;
    } else {
      throw {
        response: {
          data: {
            message: "User is not authorized to delete bills.",
            type: "error",
          },
        },
      };
    }
  } catch (error) {
    throw error;
  }
};

// Get bills for a specific branch with pagination and filtering
const getBills = (
  branchId,
  setData,
  lastDoc = null,
  limitNumber = 10,
  existingData = [],
  typeFilter = null,
  salaryTableId
) => {
  const billsCollection = collection(firestore, "Bills");

  let q = query(
    billsCollection,
    // where("branchId", "==", branchId),
    where("salaryTableId", "==", salaryTableId),
    orderBy("date", "desc")
  );

  // Add type filter if provided
  if (typeFilter) {
    q = query(q, where("type", "==", typeFilter));
  }

  // Add pagination
  if (lastDoc) {
    q = query(q, startAfter(lastDoc.date));
  }

  if (limitNumber) {
    q = query(q, limit(limitNumber));
  }

  return onSnapshot(q, (querySnapshot) => {
    const bills = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Merge the new data with the existing data
    const mergedData = lastDoc ? [...existingData, ...bills] : bills;
    setData(mergedData);
  });
};

// Search bills by reason
const searchBills = (branchId, searchText, setData) => {
  const billsCollection = collection(firestore, "Bills");

  // Note: Firestore doesn't support full-text search, so we'll need to implement
  // this differently. For now, we'll search for exact matches or use a different approach
  // This is a simplified version - in production you might want to use Algolia or similar

  const q = query(
    billsCollection,
    where("branchId", "==", branchId),
    orderBy("date", "desc")
  );

  return onSnapshot(q, (querySnapshot) => {
    const bills = querySnapshot.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      .filter((bill) =>
        bill.reason.toLowerCase().includes(searchText.toLowerCase())
      );

    setData(bills);
  });
};

export { createBill, updateBill, deleteBill, getBills, searchBills };
